import moment from 'moment';
import mapOrderStatuses from '@/constants/mapOrderStatuses';
import reasonsComment from '@/constants/reasonsComment';
import financeConstants from '@/constants/finance';
import marketingConstants from '@/constants/marketing';
import userConstants from '@/constants/users';
import Compressor from 'compressorjs';

const addCommas = (nStr) => {
	nStr += '';
	let x = nStr.split('.');
	let x1 = x[0];
	let x2 = x.length > 1 ? '.' + x[1] : '';
	let rgx = /(\d+)(\d{3})/;
	while (rgx.test(x1)) {
		x1 = x1.replace(rgx, '$1' + ',' + '$2');
	}
	return x1 + x2;
};

const sortByProp = (array, prop) => {
	return array.sort((a, b) => {
		if (typeof a[prop] === 'string') {
			return a[prop] > b[prop] ? 1 : -1;
		}
		return a[prop] - b[prop];
	});
};

const getTime = (value, is24HrFormat = true) => {
	if (value) {
		if (is24HrFormat) {
			return moment.parseZone(value).format('HH:mm:ss');
		}
		return moment.parseZone(value).format('hh:mm:ss a');
	}
};

const getShortTime = (value) => {
	return moment.parseZone(value).format('HH:mm');
};

const getTimeInFormat = (value, format) => {
	if (value) {
		return moment.parseZone(value).format(format);
	}
	return null;
};

const formatDate = (value) => {
	return moment.parseZone(value).format('MMM DD, YYYY HH:mm:ss');
};
const formatDateNoSeconds = (value) => {
	return moment.parseZone(value).format('MMM DD, YYYY HH:mm');
};

const formatShortDate = (value) => {
	return moment.parseZone(value).format('MMM DD, YYYY');
};
const formatShorterDate = (value) => {
	return moment.parseZone(value).format('MMM D');
};

const getShortDateTime = (value) => {
	return formatShortDate(value) + ' ' + getShortTime(value);
};
const formatCurrency = (value, currency = 'LEK') => {
	if (value === null || value === undefined) {
		return '';
	}
	let code = currency;
	let decimalPlaces = 0;
	if (typeof currency === 'object') {
		code = currency.code;
		decimalPlaces = currency.decimalPlaces;
	}
	value = addCommas(parseFloat(value).toFixed(decimalPlaces));
	switch (code ? code.toUpperCase() : code) {
		case 'EURO':
		case 'EUR':
			return `${value}€`;
		case 'LEK':
		case 'ALL':
			return `${value}L`;
		case 'USD':
			return `$${value}`;
	}
	return value;
};

export const serialize = function(obj, prefix) {
	let str = [],
		p;
	for (p in obj) {
		// eslint-disable-next-line no-prototype-builtins
		if (obj.hasOwnProperty(p)) {
			let k = prefix ? prefix + '[' + p + ']' : p,
				v = obj[p];
			if (v !== null && v !== undefined) {
				str.push(typeof v === 'object' ? serialize(v, k) : encodeURIComponent(k) + '=' + encodeURIComponent(v));
			}
		}
	}
	return str.join('&');
};

const extractErrorMessage = (error) => {
	if (typeof error === 'string') {
		return error;
	}
	if (error['notes']) {
		error = error['notes'];
	}
	if (error.body) {
		error = error.body.error;
	}
	if (error.data) {
		error = error.data;
	}
	if (typeof error === 'string') {
		return error;
	}
	if (typeof error.message === 'string') {
		return error.message;
	}
	try {
		Object.keys(error.message).forEach((key) => {
			if (typeof error.message[key] === 'string') {
				return error.message[key];
			} else {
				error.message[key].forEach((message) => {
					return message;
				});
			}
		});
	} catch (e) {
		return 'An error occurred during login. Please try again.';
	}
};

export const prepAutocompleteData = (data, labelProp, valueProp) => {
	return data.map((i) => {
		let label = '';
		if (Array.isArray(labelProp)) {
			labelProp.map((l) => {
				label = `${label} ${i[l]}`;
			});
		} else {
			label = i[labelProp];
		}
		return {
			...i,
			label,
			value: i[valueProp],
		};
	});
};

export const prepOptionValues = (data) => {
	return data.map((type) => {
		let _cType = Object.entries(type)[0];
		let key = _cType[0];
		let label = _cType[1];
		return { id: key, value: key, label: label };
	});
};

export const calcProgressPercentage = (value, comparedValue) => {
	if (!comparedValue) {
		return 0;
	}
	if (value === 0 && comparedValue === 0) return 0;
	else if (comparedValue === 0) return 100;
	else {
		return ((value / comparedValue - 1) * 100).toFixed(2);
	}
};

const orderStatus = (value) => {
	const status = mapOrderStatuses.find((c) => c.value === value);
	if (status) {
		return status.label;
	}
	return value;
};

const reasonFilter = (value) => {
	const reason = reasonsComment.find((r) => r.value === value);
	if (reason && reason.label) {
		return reason.label;
	}
	return value;
};

const dynamicSortBy = (a, b, key, direction = 'asc') => {
	const dir = direction === 'asc' ? 1 : -1;
	const keyPath = key.split('.');
	let value = a;
	let comparedValue = b;

	keyPath.map((piece) => {
		value = value[piece];
		comparedValue = comparedValue[piece];
	});

	if (typeof value === 'string') {
		value = value.toLowerCase();
		comparedValue = comparedValue.toLowerCase();
		if (value > comparedValue) {
			return -dir;
		}
		if (value < comparedValue) {
			return dir;
		}
		return 0;
	}
	if (typeof value === 'number') {
		return dir * (value - comparedValue);
	}
	return dir * (moment(value) - moment(comparedValue));
};

const dynamicFilter = (item, columns, value) => {
	if (!value) {
		return true;
	}

	let searchTerm = value.toLowerCase().split(' ');
	searchTerm = searchTerm.filter((s) => s !== '');
	searchTerm.push(value.toLowerCase());

	const checkNestedValues = (obj) => {
		if (!obj || typeof obj !== 'object') return false;
		return Object.values(obj).some((value) => {
			if (value && typeof value === 'object') {
				return checkNestedValues(value);
			}
			return (
				value &&
				searchTerm.some(
					(search) =>
						value
							.toString()
							.toLowerCase()
							.indexOf(search) > -1
				)
			);
		});
	};
	if (checkNestedValues(item)) return true;

	const _hasOneOf = (prop) => {
		const pieces = value.split(','); // Replace split if multiple search should be with ' ' or '&'
		pieces.push(value);

		return (
			pieces.filter((p) => {
				const search = p.toString().trim();
				return (
					p &&
					search &&
					prop
						.toString()
						.toLowerCase()
						.indexOf(search) > -1
				);
			}).length > 0
		);
	};

	const hasVal = columns.filter(({ key, keyProp, skipSearch }) => {
		if (skipSearch) {
			return false;
		}
		const _key = keyProp ? keyProp : key;
		const keyPath = _key.split('.');
		let propValue = item;
		keyPath.map((piece) => {
			propValue = propValue[piece];
		});
		return propValue && propValue.toString().toLowerCase().indexOf && _hasOneOf(propValue);
	});
	return hasVal.length > 0;
};

const setImage = (url) => {
	if (!url) {
		return '';
	}
	return url.replace('https', 'http').replace('http://', 'https://images.weserv.nl/?url=');
};

const fromNow = (date) => {
	return moment(date).fromNow();
};

const paymentTerm = (value) => {
	const ret = financeConstants.paymentTermTypes.find((p) => p.value === value);
	if (ret) {
		return ret.label;
	}
	return value;
};

const entityType = (value) => {
	if (value === marketingConstants.entityTypeRestaurant) {
		return 'Restaurant';
	} else {
		return 'Category';
	}
};

const userType = (value) => {
	if (userConstants.userTypes.names[value]) {
		return userConstants.userTypes.names[value];
	}
	return value;
};

const isMobile = () => {
	return window.matchMedia('only screen and (max-width: 760px)').matches;
};

const getBase64 = (file, ignoreCompression) => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			resolve(e.target.result);
		};
		if (ignoreCompression) {
			return reader.readAsDataURL(file);
		} else {
			new Compressor(file, {
				quality: 0.3,
				// The compression process is asynchronous,
				// which means you have to access the `result` in the `success` hook function.
				success(file) {
					reader.readAsDataURL(file);
				},
				error(err) {
					console.log(err.message);
				},
			});
		}
	});
};

const reOrderItems = (elements, oldIndex, newIndex) => {
	const step = 10;
	let items = elements.map((i, index) => ({
		...i,
		_custom_order: step * index,
	}));
	items[oldIndex]._custom_order = step * newIndex - oldIndex + newIndex;
	return sortByProp(items, '_custom_order');
};

const encrypt = (value) => {
	return window.btoa(value);
};

const decrypt = (value) => {
	return window.atob(value);
};

export const areEqual = (array1, array2) => {
	if (array1.length === array2.length) {
		return array1.every((element) => {
			return !!array2.includes(element);
		});
	}
	return false;
};

const capitalization = (value) => {
	if (typeof value === 'string' && value.includes('_')) {
		return value
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	}
	if (typeof value === 'string') {
		return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
	}
	return value;
};

const returnReason = (closingTime) => {
	return `Reason: ${closingTime.reason ? capitalization(closingTime.reason) : ''} ${
		closingTime.notes ? '\nNotes: ' + closingTime.notes : ''
	} \n${
		closingTime.startDate
			? `Will be Offline from ${formatDate(closingTime.startDate)} to ${formatDate(closingTime.endDate)}`
			: new Date(closingTime.endDate).getFullYear() === 2100
			? 'Offline with Undefined end date'
			: `Offline until ${formatDate(closingTime.endDate)}`
	}`;
};

export const formatDynamicColumnName = (name) => {
	return name
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2')
		.replace(/[_,]/g, ' ')
		.trim()
		.replace(/^./, (str) => str.toUpperCase());
};

export default {
	sortByProp,
	serialize,
	extractErrorMessage,
	filters: {
		addCommas,
		getTime,
		getShortTime,
		getTimeInFormat,
		formatCurrency,
		formatDate,
		formatShortDate,
		orderStatus,
		reasonFilter,
		setImage,
		fromNow,
		paymentTerm,
		entityType,
		userType,
		formatDateNoSeconds,
		formatDynamicColumnName,
		formatShorterDate,
		getShortDateTime,
		capitalization,
		returnReason,
	},
	calcProgressPercentage,
	dynamicSortBy,
	dynamicFilter,
	isMobile,
	getBase64,
	reOrderItems,
	encrypt,
	decrypt,
};

// const formatter = new Intl.NumberFormat('en-IN');
